/**
 * MiniMonth — the month-grid date navigator every calendar carries (Google
 * Calendar's left rail, Outlook's date picker).
 *
 * Stepping a timeline a week at a time answers "next week"; it does not
 * answer "the weekend of the 19th". This is the jump control: a month of
 * days, the current window drawn as one capsule across them, a click to
 * select a single day, and a drag across days to set a range. Month and
 * year are pickers rather than arrows alone, so a date two years out is one
 * choice away instead of twenty-four clicks.
 *
 * Dates are handled as UTC-anchored `YYYY-MM-DD` strings — the same shape
 * the schedule API speaks — so a local timezone never shifts a cell onto the
 * wrong day.
 */
import { useEffect, useRef, useState } from "react";
import { cx } from "@shared/cx";
import { ChevronLeftIcon, ChevronRightIcon } from "./icons";
import { Select } from "./Select";
import styles from "./MiniMonth.module.css";

interface MiniMonthProps {
  /** First day of the timeline window. */
  start: string;
  /** Last day of the timeline window, inclusive. */
  end: string;
  /** Today in the company's timezone, always marked. */
  today: string;
  /** A plain click: select that day alone. */
  onPick: (iso: string) => void;
  /** A drag across days: set the window's start and end. */
  onRange: (start: string, end: string) => void;
}

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Years offered either side of the displayed one. */
const YEAR_SPAN = 5;

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** The Monday on or before the first of `month` (`YYYY-MM`). */
function gridStart(month: string): Date {
  const first = new Date(`${month}-01T00:00:00Z`);
  const offset = (first.getUTCDay() + 6) % 7;
  first.setUTCDate(first.getUTCDate() - offset);
  return first;
}

function shiftMonth(month: string, by: number): string {
  const d = new Date(`${month}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + by);
  return iso(d).slice(0, 7);
}

export function MiniMonth({
  start,
  end,
  today,
  onPick,
  onRange,
}: MiniMonthProps) {
  const [month, setMonth] = useState(start.slice(0, 7));
  const [drag, setDrag] = useState<{ anchor: string; hover: string } | null>(
    null,
  );
  /**
   * The range just committed, painted until the props confirm it. The commit
   * travels through the URL, and react-router wraps every navigation in
   * `startTransition` — so the sync drag-clear paints before the new window
   * arrives. Falling back to props in that gap redraws the *previous*
   * window for a frame or two (select → unselect → reselect).
   */
  const [pending, setPending] = useState<[string, string] | null>(null);
  /** Set once a pointer sequence has committed, so its trailing click is a no-op. */
  const handled = useRef(false);

  // Following the window means the grid shows what the timeline shows —
  // typing a date elsewhere, or stepping the window, brings its month here.
  useEffect(() => {
    setMonth(start.slice(0, 7));
  }, [start]);

  // The props caught up (or the window changed some other way) — either way
  // they now own the display again.
  useEffect(() => {
    setPending(null);
  }, [start, end]);

  // The drag ends wherever the pointer is released, which is routinely
  // outside the grid — so the commit listens on the window, not on a cell.
  //
  // The move handler is on the window too, and hit-tests the point rather
  // than relying on `pointerenter` firing per cell: a touch implicitly
  // captures the pointer to the element it started on, so enter events never
  // reach the other days and every touch would resolve to a one-day pick.
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const day = el?.closest<HTMLElement>("[data-day]")?.dataset.day;
      if (day && day !== drag.hover) setDrag({ ...drag, hover: day });
    };
    const up = () => {
      const [a, b] = [drag.anchor, drag.hover].sort();
      handled.current = true;
      setDrag(null);
      setPending([a, b]);
      if (a !== b) onRange(a, b);
      else onPick(a);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [drag, onPick, onRange]);

  const [rangeStart, rangeEnd] = drag
    ? [drag.anchor, drag.hover].sort()
    : (pending ?? [start, end]);

  const gridFrom = gridStart(month);
  const cells: string[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridFrom);
    d.setUTCDate(d.getUTCDate() + i);
    cells.push(iso(d));
  }

  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7)) - 1;
  const years = Array.from(
    { length: YEAR_SPAN * 2 + 1 },
    (_, i) => year - YEAR_SPAN + i,
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.pickers}>
          <Select
            variant="quiet"
            ariaLabel="Month"
            value={String(monthIndex)}
            options={MONTHS.map((label, i) => ({ value: String(i), label }))}
            onChange={(next) =>
              setMonth(`${year}-${String(Number(next) + 1).padStart(2, "0")}`)
            }
          />
          <Select
            variant="quiet"
            ariaLabel="Year"
            value={String(year)}
            options={years.map((y) => ({ value: String(y), label: String(y) }))}
            onChange={(next) => setMonth(`${next}-${month.slice(5, 7)}`)}
          />
        </div>
        <div className={styles.steppers}>
          <button
            type="button"
            aria-label="Previous month"
            onClick={() => setMonth(shiftMonth(month, -1))}
            className={styles.stepper}
          >
            <ChevronLeftIcon className={styles.stepperIcon} />
          </button>
          <button
            type="button"
            aria-label="Next month"
            onClick={() => setMonth(shiftMonth(month, 1))}
            className={styles.stepper}
          >
            <ChevronRightIcon className={styles.stepperIcon} />
          </button>
        </div>
      </div>

      {/* The grid suppresses touch scrolling so a drag across days selects —
          see `.grid` in the module. */}
      <div className={styles.grid}>
        {WEEKDAYS.map((label, i) => (
          <div key={i} className={styles.weekday}>
            {label}
          </div>
        ))}
        {cells.map((day) => {
          const outside = !day.startsWith(month);
          const inRange = day >= rangeStart && day <= rangeEnd;
          const isToday = day === today;
          // The capsule and the day's own mark: see the module, which carries
          // why the run is one capsule rather than a row of boxes and why
          // exactly one day is ever filled green.
          const capsule = inRange
            ? cx(
                styles.inRange,
                day === rangeStart && styles.rangeStart,
                day === rangeEnd && styles.rangeEnd,
              )
            : "";
          const mark = isToday
            ? styles.today
            : inRange
              ? styles.selected
              : outside
                ? styles.outside
                : styles.plain;
          return (
            <button
              key={day}
              type="button"
              aria-label={day}
              aria-pressed={inRange}
              data-day={day}
              onPointerDown={(e) => {
                // Touch captures the pointer to this button by default, which
                // would starve the window move handler of any other target.
                if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
                  e.currentTarget.releasePointerCapture(e.pointerId);
                }
                handled.current = false;
                setDrag({ anchor: day, hover: day });
              }}
              // Keyboard only: a pointer press has already committed above.
              onClick={() => {
                if (handled.current) handled.current = false;
                else {
                  setPending([day, day]);
                  onPick(day);
                }
              }}
              className={styles.day}
            >
              {/*
                The selection band is deliberately shorter than today's disc,
                so today still reads as the one marked day even when it sits
                inside the selection.
              */}
              <span className={cx(styles.band, capsule)}>
                <span className={cx(styles.mark, mark)}>
                  {Number(day.slice(8))}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
